import { useMemo, useState } from 'react';
import { X, Lightbulb, Check, Printer } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import StellungnahmeBausteine from './StellungnahmeBausteine';
import StellungnahmePositionen from './StellungnahmePositionen';

type Any = Record<string, any>;

interface Section {
  key: string;
  label: string;
  text: string;
  tipp: string;
}

const fmtDate = (d: any) => (d ? new Date(d).toLocaleDateString('de-DE') : '');
const eur = (v: any) =>
  v == null || v === '' ? '' : `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

function defaultSections(akte: Any, sn: Any): Section[] {
  const istHonorar = sn?.art === 'honorar';
  const fahrzeug = [akte?.fahrzeug_marke, akte?.fahrzeug_modell].filter(Boolean).join(' ');
  const kennz = akte?.fahrzeug_kennzeichen || akte?.kennzeichen || '';
  const schadendatum = fmtDate(akte?.schadendatum || akte?.schadentag);
  const kunde = akte?.kunde_name || '';
  const az = akte?.akten_nr || akte?.akten_nummer || akte?.gutachten_aktenzeichen || sn?.aktenzeichen_ag || '';
  const versicherung = akte?.versicherung || 'der gegnerischen Versicherung';
  const anwalt = akte?.anwalt_name || '';

  if (istHonorar) {
    const kBetrag = eur(akte?.kuerzung_betrag);
    const kGrund = akte?.kuerzung_grund ? ` (Begründung: „${akte.kuerzung_grund}“)` : '';
    return [
      {
        key: 'anrede',
        label: 'Anrede',
        text: 'Sehr geehrte Damen und Herren,',
        tipp: 'Versicherung wird i.d.R. unpersönlich angeschrieben.',
      },
      {
        key: 'sachverhalt',
        label: 'Sachverhalt',
        text: `in der oben genannten Schadenangelegenheit${
          akte?.schadennummer ? ` (Schaden-Nr. ${akte.schadennummer})` : ''
        } haben wir das Schadengutachten${az ? ` (Az. ${az})` : ''} erstellt und Ihnen unsere Sachverständigenrechnung übersandt.`,
        tipp: 'Kurz: welches Gutachten, welche Rechnung.',
      },
      {
        key: 'wuerdigung',
        label: 'Würdigung',
        text: `Sie haben unser Sachverständigenhonorar${kBetrag ? ` um ${kBetrag}` : ''} gekürzt${kGrund}. Dieser Kürzung widersprechen wir. Die Höhe unseres Honorars orientiert sich an der Schadenhöhe und bewegt sich im Rahmen der BVSK-Honorarbefragung; sie ist üblich und angemessen.`,
        tipp: 'Kern: warum die Honorarkürzung nicht gerechtfertigt ist (BVSK, Schadenhöhe).',
      },
      {
        key: 'forderung',
        label: 'Forderung',
        text: 'Wir bitten um Ausgleich des noch offenen Restbetrags innerhalb von 14 Tagen.',
        tipp: 'Klare Frist nennen.',
      },
      {
        key: 'schluss',
        label: 'Schluss',
        text: 'Mit freundlichen Grüßen\n\nAndreas Koop\nKfz Techniker-Meister\nKfz Sachverständiger – Mitglied im MAS\nBVSK Akademie',
        tipp: 'Grußformel + Unterschrift.',
      },
    ];
  }

  // Kunde / Anwalt
  let wuerdigung = '';
  if (akte?.rep_kosten_gekuerzt) {
    wuerdigung += `Die ${versicherung} hat die im Gutachten ausgewiesenen Reparaturkosten${
      eur(akte?.rep_kosten_kuerzung_betrag) ? ` um ${eur(akte.rep_kosten_kuerzung_betrag)}` : ''
    } gekürzt${akte?.rep_kosten_kuerzung_grund ? ` mit der Begründung „${akte.rep_kosten_kuerzung_grund}“` : ''}. Aus sachverständiger Sicht ist diese Kürzung nicht gerechtfertigt: [Begründung ergänzen].`;
  }
  if (akte?.nachbesichtigung_gefordert) {
    wuerdigung += `${wuerdigung ? '\n\n' : ''}Seitens der Versicherung wird eine Nachbesichtigung des Fahrzeugs gefordert. Die im Gutachten dokumentierten Schäden sind durch Lichtbilder eindeutig belegt; eine Nachbesichtigung ist daher nicht erforderlich.`;
  }
  if (akte?.regulierungsproblem) {
    wuerdigung += `${wuerdigung ? '\n\n' : ''}${akte.regulierungsproblem}`;
  }
  if (!wuerdigung) wuerdigung = '[Hier die fachliche/rechtliche Würdigung ergänzen.]';

  return [
    {
      key: 'anrede',
      label: 'Anrede',
      text: anwalt ? `Sehr geehrter Herr Rechtsanwalt ${anwalt},` : 'Sehr geehrte Damen und Herren,',
      tipp: 'Anwälte mit Namen anreden, Kunden je nach Verhältnis.',
    },
    {
      key: 'sachverhalt',
      label: 'Sachverhalt',
      text: `in der oben genannten Schadenangelegenheit nehmen wir als Kfz-Sachverständige wie folgt Stellung.\n\nWir wurden von ${
        kunde || 'unserem Auftraggeber'
      } mit der Erstellung eines Schadengutachtens beauftragt. Am ${
        schadendatum || '[Schadendatum]'
      } ereignete sich ein Verkehrsunfall, bei dem das Fahrzeug ${fahrzeug || '[Fahrzeug]'}${
        kennz ? ` (${kennz})` : ''
      } beschädigt wurde. Unser Gutachten trägt das Aktenzeichen ${az || '[Az]'}.`,
      tipp: 'Kurz und faktisch: wer, wann, welches Fahrzeug, welches Az.',
    },
    {
      key: 'wuerdigung',
      label: 'Würdigung',
      text: wuerdigung,
      tipp: 'Der Kern. Sachlich begründen, warum die Kürzung fachlich nicht haltbar ist.',
    },
    {
      key: 'forderung',
      label: 'Forderung',
      text: 'Wir bitten Sie daher, die Schadenpositionen vollständig anzuerkennen und den noch offenen Betrag zur Auszahlung zu bringen.',
      tipp: 'Klar formulieren, was verlangt wird (vollständige Regulierung / Frist).',
    },
    {
      key: 'schluss',
      label: 'Schluss',
      text: 'Mit freundlichen Grüßen\n\nAndreas Koop\nKfz Techniker-Meister\nKfz Sachverständiger – Mitglied im MAS\nBVSK Akademie',
      tipp: 'Grußformel + Unterschrift.',
    },
  ];
}

interface Props {
  akte: Any;
  stellungnahme: Any;
  onClose: () => void;
  onSaved?: () => void;
}

export default function StellungnahmeEditor({ akte, stellungnahme, onClose, onSaved }: Props) {
  const initial = useMemo<Section[]>(() => {
    const saved = stellungnahme?.antworten;
    if (Array.isArray(saved) && saved.length > 0 && saved[0]?.key) return saved as Section[];
    return defaultSections(akte, stellungnahme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stellungnahme?.id]);

  const [sections, setSections] = useState<Section[]>(initial);
  const [bestaetigt, setBestaetigt] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const alleBestaetigt = sections.every((s) => bestaetigt[s.key]);

  function setText(key: string, text: string) {
    setSections((prev) => prev.map((s) => (s.key === key ? { ...s, text } : s)));
  }

  function neuVorschlagen() {
    setSections(defaultSections(akte, stellungnahme));
    setBestaetigt({});
    setMsg('Neue Vorschläge eingesetzt.');
    setTimeout(() => setMsg(null), 2000);
  }

  function buildText(): string {
    return sections.map((s) => s.text.trim()).filter(Boolean).join('\n\n');
  }

  function insertBaustein(text: string) {
    setSections((prev) =>
      prev.map((s) => {
        if (s.key !== 'wuerdigung') return s;
        const base = s.text && !s.text.trim().startsWith('[') ? s.text.trimEnd() + '\n\n' : '';
        return { ...s, text: base + text };
      }),
    );
  }

  function druckenPDF() {
    const a: any = akte || {};
    const esc = (v: any) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
    const heute = new Date().toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
    const kennz = a.fahrzeug_kennzeichen || a.kennzeichen || '';
    const aktennr = a.akten_nr || a.akten_nummer || '';
    const origin = window.location.origin;
    const vn = a.versicherungsnummer || a.schadennummer || '';
    const schadentag = a.schadentag || a.schadendatum;
    const schadentagFmt = schadentag ? new Date(schadentag).toLocaleDateString('de-DE') : '';
    const empfLines = [stellungnahme?.empfaenger_kanzlei || (a.kunde_name ? `Herr ${a.kunde_name}` : '')].filter(Boolean);
    const links: [string, string][] = [
      ['Aktenzeichen', aktennr],
      ['Fz.-Ident.Nr', a.fahrzeug_vin || ''],
      ['Gegn. Versicherung', a.versicherung || ''],
    ];
    const rechts: [string, string][] = [
      ['Vers.-Nummer', vn],
      ['Schadenort', a.schadenort || ''],
      ['Schadentag', schadentagFmt],
      ['Kennz. Unfallgegner', a.kennzeichen_gegner || ''],
    ];
    const rowHtml = (pairs: [string, string][]) =>
      pairs.filter(([, v]) => v).map(([l, v]) => `<tr><td class="dl">${esc(l)}</td><td class="dv">${esc(v)}</td></tr>`).join('');
    const bodyHtml = buildText().split(/\n{2,}/).map((p) => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`).join('');
    const titelAz = aktennr || stellungnahme?.aktenzeichen_ag || '';
    const html = `<!doctype html><html lang="de"><head><meta charset="utf-8"><title>Stellungnahme ${esc(aktennr)}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;1,400&display=swap');
  @page { size: A4; margin: 18mm 18mm 16mm 22mm; }
  * { box-sizing: border-box; }
  body { font-family:'Open Sans',Arial,sans-serif; color:#1a1a1a; font-size:10.5pt; line-height:1.55; margin:0; }
  .head { display:flex; align-items:stretch; gap:8px; margin-bottom:10px; }
  .head .logo { display:flex; align-items:center; }
  .head .logo img { height:18mm; width:auto; object-fit:contain; }
  .head .banner { flex:1; background:#1f3a5c; color:#fff; text-align:right; padding:8px 14px; display:flex; flex-direction:column; justify-content:center; }
  .head .banner .b1 { font-weight:600; font-size:14pt; }
  .head .banner .b2 { font-weight:600; font-size:10.5pt; }
  .head .banner .b3 { font-size:8.5pt; font-weight:300; }
  .az { display:flex; justify-content:space-between; font-size:9pt; font-weight:600; border-bottom:0.5pt solid #1f3a5c; padding-bottom:3px; margin:4px 0 12px; }
  .addr { display:flex; justify-content:space-between; gap:20px; margin-bottom:14px; }
  .addr .ret { font-size:9.5pt; }
  .addr .ret .small { font-size:8pt; color:#666; margin-bottom:10px; }
  .addr .ret .empf { font-weight:600; }
  .addr .send { font-size:8.5pt; color:#555; min-width:56mm; }
  h1 { font-size:14pt; font-weight:600; margin:6px 0 4px; }
  .kennz { font-weight:600; font-size:10.5pt; margin:0 0 8px; }
  .grid { display:flex; gap:14mm; margin-bottom:12px; }
  table.fd { border-collapse:collapse; }
  table.fd td { padding:1px 0; font-size:9pt; vertical-align:top; }
  table.fd td.dl { color:#666; width:34mm; }
  table.fd td.dv { color:#1a1a1a; font-weight:600; padding-right:8mm; }
  p { text-align:justify; margin:0 0 8px; }
</style></head><body>
  <div class="head">
    <div class="logo"><img src="${origin}/logo-dark.png" alt="KOOP" onerror="this.style.display='none'"></div>
    <div class="banner"><div class="b1">Kfz Sachverständigenbüro Koop</div><div class="b2">Inh. Andreas Koop</div><div class="b3">Kfz-Techniker-Meister &amp; Kfz-Sachverständiger</div></div>
  </div>
  <div class="az"><span>AZ: ${esc(aktennr)}${kennz ? '  |  ' + esc(kennz) : ''}${vn ? '  |  VN: ' + esc(vn) : ''}</span><span>Iserlohn, ${esc(heute)}</span></div>
  <div class="addr">
    <div class="ret">
      <div class="small">Kfz Sachverständigenbüro Koop · Rahmedestraße 65, 58507 Lüdenscheid</div>
      ${empfLines.map((l) => `<div class="empf">${esc(l)}</div>`).join('')}
    </div>
    <div class="send">Kfz Sachverständigenbüro Koop<br>Inhaber Andreas Koop<br>Kfz-Techniker-Meister &amp; Kfz Sachverständiger<br>Ausgebildet an der BVSK Akademie<br><br>Mobil: 0170/500 40 22<br>Email: info@gutachter-koop.de</div>
  </div>
  <h1>Stellungnahme zum Gutachten ${esc(titelAz)}</h1>
  ${kennz ? `<div class="kennz">Amtl. Kennzeichen: ${esc(kennz)}</div>` : ''}
  <div class="grid">
    <table class="fd">${rowHtml(links)}</table>
    <table class="fd">${rowHtml(rechts)}</table>
  </div>
  ${bodyHtml}
</body></html>`;
    const w = window.open('', '_blank');
    if (!w) { setMsg('Bitte Popups erlauben.'); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch { /* ignore */ } }, 500);
  }

  async function speichern() {
    setSaving(true);
    setMsg(null);
    const { error } = await supabase
      .from('stellungnahmen')
      .update({
        antworten: sections,
        generierter_text: buildText(),
        generiert_am: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', stellungnahme.id);
    setSaving(false);
    if (error) {
      setMsg('Fehler: ' + error.message);
      return;
    }
    setMsg('Gespeichert.');
    onSaved?.();
    setTimeout(() => {
      setMsg(null);
      onClose();
    }, 800);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl my-6">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <div>
            <h2 className="text-base font-semibold text-gray-800">
              {stellungnahme?.art === 'honorar' ? 'Honorar-Stellungnahme' : 'Stellungnahme'} bearbeiten
            </h2>
            <p className="text-xs text-gray-500">{stellungnahme?.betreff}</p>
          </div>
          <div className="flex items-center gap-3">
            {msg && <span className="text-xs text-gray-500">{msg}</span>}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Vorschläge aus den Akten-/Übersichtsdaten. Jeden Abschnitt anpassen und bestätigen.
            </p>
            <button
              type="button"
              onClick={neuVorschlagen}
              className="text-xs font-medium text-[#B91C1C] hover:underline shrink-0"
            >
              Neu vorschlagen
            </button>
          </div>

          {sections.map((s) => (
            <div key={s.key} className="border border-gray-200 rounded-xl p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-gray-700">{s.label}</span>
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!bestaetigt[s.key]}
                    onChange={(e) => setBestaetigt((b) => ({ ...b, [s.key]: e.target.checked }))}
                  />
                  bestätigt
                </label>
              </div>
              <textarea
                className={
                  'w-full rounded-lg border px-3 py-2 text-sm min-h-[80px] ' +
                  (bestaetigt[s.key] ? 'border-green-300 bg-green-50/40' : 'border-gray-300')
                }
                value={s.text}
                onChange={(e) => setText(s.key, e.target.value)}
              />
              <p className="mt-1 flex items-start gap-1.5 text-xs text-amber-700">
                <Lightbulb className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{s.tipp}</span>
              </p>
            </div>
          ))}

          <StellungnahmeBausteine
            akte={akte}
            art={stellungnahme?.art}
            stellungnahmeId={stellungnahme?.id}
            onInsert={insertBaustein}
          />

          <StellungnahmePositionen akte={akte} stellungnahme={stellungnahme} />

          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Vorschau</span>
            <pre className="mt-1 whitespace-pre-wrap text-sm text-gray-800 bg-gray-50 rounded-lg border border-gray-200 p-3">
              {buildText()}
            </pre>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 px-5 py-3">
          <span className="text-xs text-gray-500 flex items-center gap-1.5">
            {alleBestaetigt ? (
              <>
                <Check className="w-4 h-4 text-green-600" /> Alle Abschnitte bestätigt
              </>
            ) : (
              'Noch nicht alle Abschnitte bestätigt'
            )}
          </span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg">
              Schließen
            </button>
            <button
              onClick={druckenPDF}
              className="px-3 py-1.5 text-sm font-medium text-[#1f3a5c] border border-[#1f3a5c]/40 hover:bg-[#1f3a5c]/5 rounded-lg flex items-center gap-1.5"
            >
              <Printer className="w-4 h-4" /> Drucken / PDF
            </button>
            <button
              onClick={speichern}
              disabled={saving}
              className="px-4 py-1.5 text-sm font-medium text-white bg-[#B91C1C] hover:bg-[#991B1B] rounded-lg disabled:opacity-50"
            >
              {saving ? 'Speichert…' : 'Speichern'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
